import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import Select from 'react-select';
import { toast, ToastContainer } from 'react-toastify';
import axios from 'axios';
import './css/project.css';
import {
    Info, FlaskConical, Calendar, Hash, FileText,
    Building2, Users, ChevronRight, AlertCircle, Loader, Save, X,
    Plus, Trash2, MessageSquare, UserRound, Check,
    Sparkles, Clock, Target, FileCheck, PenLine, Briefcase,
    SectionIcon,
    ClipboardList,
    AlertTriangle
} from "lucide-react"; import { BASE_URL } from '../../config/axios';
import { CSSForSelectModern, handleErrorToast } from '../../components/CustomFunctions';
import AddModalButton, { DeleteModalButton, UpdateModalButton } from '../../components/DynamicButtons';

const initialFormData = {
    project_code: "",
    project_name: "",
    project_status: "",
    client_detail: "",
    project_scope: "",
    test_performed: "",
    lab_sample_quantity: "0",
    department_equipment: "",
    responsible_person: "",
    testing_person: "",
    project_confirmation_date: null,
    mobilization_date: null,
    survey_date: null,
    completion_date: null,
    sample_receiving_date_ho: null,
    project_data_received_from_client: null,
    draft_report_submission_date: null,
    final_report_submission_date: null,
    total_duration: 0,
    remark_1: "",
    remark_2: "",
    project_description: "",
    test_planned_status: false,
    report_revised_status: false,
    revised_reason: "",
    revised_date: null,
    test_planned_date: null,
    client_concern_person: "",
    remarks: [],
};

const todayStr = () => new Date().toISOString().split("T")[0];

const formatDate = (d) => {
    if (!d) return "-";
    const [y, m, day] = d.split("-");
    return `${day}-${m}-${y}`;
};

const fieldLabels = {
    project_name: "Project name",
    project_status: "Project status",
    client_detail: "Client detail",
    test_performed: "Test performed",
    responsible_person: "Responsible person",
    survey_date: "Survey date",
    revised_date: "Revised date",
    revised_reason: "Revised reason",
};

const ToggleSwitch = ({ checked, onChange, label, description }) => (
    <label className="toggle-card">
        <div className="toggle-text">
            <strong>{label}</strong>
            {description && <small>{description}</small>}
        </div>
        <span className="toggle-switch">
            <input type="checkbox" checked={checked} onChange={onChange} />
            <span className="toggle-slider" />
        </span>
    </label>
);

const UpdateProjectDetails = ({ getProjects, i }) => {
    const [show, setShow] = useState(false);
    const [activeSection, setActiveSection] = useState('basic');
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});
    const [tabValidationAttempted, setTabValidationAttempted] = useState({});
    const [newRemark, setNewRemark] = useState({ date: todayStr(), remarks: "" });
    const [remarkError, setRemarkError] = useState("");

    const sections = [
        { id: 'basic', label: 'Basic Info', icon: Info, color: '#3b82f6' },
        { id: 'testing', label: 'Testing Details', icon: FlaskConical, color: '#8b5cf6' },
        { id: 'timeline', label: 'Timeline', icon: Calendar, color: '#f59e0b' },
        { id: 'remarks', label: 'Remarks', icon: MessageSquare, color: '#10b981' },
    ];

    const [formData, setFormData] = useState(initialFormData);

    const handleShow = () => {
        setShow(true);
        setActiveSection('basic');
        setFormData({
            ...initialFormData,
            ...i,
            project_description: i?.project_description && i.project_description !== "null" ? i.project_description : "",
            client_concern_person: i?.client_concern_person || "",
            revised_reason: i?.revised_reason || "",
            test_planned_status: !!i?.test_planned_status,
            report_revised_status: !!i?.report_revised_status,
            test_planned_date: i?.test_planned_status ? i?.test_planned_date || null : null,
            revised_date: i?.report_revised_status ? i?.revised_date || null : null,
            remarks: Array.isArray(i?.remarks) ? i.remarks : [],
        });
    };

    const handleClose = () => {
        setFormData(initialFormData);
        setErrors({});
        setInputState({});
        setTabValidationAttempted({});
        setNewRemark({ date: todayStr(), remarks: "" });
        setRemarkError("");
        setShow(false);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target || e;
        setInputState((prev) => ({ ...prev, [name]: value?.toString().trim() ? "green" : "" }));
        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => {
            const updated = { ...prev };
            if (value) delete updated[name];
            return updated;
        });
    };

    const handleToggle = (name) => (e) => {
        const checked = e.target.checked;
        setFormData((prev) => {
            const next = { ...prev, [name]: checked };
            if (name === "test_planned_status" && !checked) next.test_planned_date = null;
            if (name === "report_revised_status" && !checked) {
                next.revised_date = null;
                next.revised_reason = "";
            }
            return next;
        });
        if (!checked && name === "report_revised_status") {
            setErrors((prev) => {
                const updated = { ...prev };
                delete updated.revised_date;
                delete updated.revised_reason;
                return updated;
            });
        }
    };

    const handleAddRemark = () => {
        if (!newRemark.date) { setRemarkError("Please select a date"); return; }
        if (!newRemark.remarks.trim()) { setRemarkError("Please write a remark before adding"); return; }
        const entry = {
            id: Date.now().toString(),
            date: newRemark.date,
            remarks: newRemark.remarks.trim(),
            created_at: new Date().toISOString(),
        };
        setFormData((prev) => ({ ...prev, remarks: [...prev.remarks, entry] }));
        setNewRemark({ date: todayStr(), remarks: "" });
        setRemarkError("");
    };

    const handleDeleteRemark = (id) => {
        setFormData((prev) => ({ ...prev, remarks: prev.remarks.filter((r) => r.id !== id) }));
    };

    const projectStatusOptions = [
        { value: "Open", label: "Open" },
        { value: "Ongoing", label: "Ongoing" },
        { value: "Closed", label: "Closed" },
    ];

    const tabRequiredFields = {
        basic: ["project_name", "project_status", "client_detail"],
        testing: ["responsible_person"],
        timeline: ["test_performed", "survey_date"],
        remarks: [],
    };

    const getRequiredFields = (tab) => {
        const base = tabRequiredFields[tab] || [];
        if (tab === "timeline" && formData.report_revised_status) {
            return [...base, "revised_date", "revised_reason"];
        }
        return base;
    };

    const getFieldError = (field) => `${fieldLabels[field] || field} is required!`;

    const validateCurrentTab = () => {
        const currentTab = activeSection;
        const newErrors = {};
        getRequiredFields(currentTab).forEach((field) => {
            if (!formData[field]) newErrors[field] = getFieldError(field);
        });
        setErrors((prev) => ({ ...prev, ...newErrors }));
        setTabValidationAttempted((prev) => ({ ...prev, [currentTab]: true }));
        return Object.keys(newErrors).length === 0;
    };

    const validateForm = () => {
        const newErrors = {};
        sections.forEach((s) => {
            getRequiredFields(s.id).forEach((field) => {
                if (!formData[field]) newErrors[field] = getFieldError(field);
            });
        });
        setErrors(newErrors);
        const firstBadTab = sections.find((s) => getRequiredFields(s.id).some((f) => newErrors[f]));
        if (firstBadTab) {
            setActiveSection(firstBadTab.id);
            setTabValidationAttempted((prev) => ({ ...prev, [firstBadTab.id]: true }));
            const formContainer = document.querySelector('.modern-modal-body');
            if (formContainer) formContainer.scrollTop = 0;
        }
        return Object.keys(newErrors).length === 0;
    };

    const goToNextTab = () => {
        const currentIndex = sections.findIndex((s) => s.id === activeSection);
        const nextTab = sections[currentIndex + 1]?.id;
        if (!validateCurrentTab()) {
            const formContainer = document.querySelector('.modern-modal-body');
            if (formContainer) formContainer.scrollTop = 0;
            return;
        }
        if (nextTab) {
            setErrors((prev) => {
                const filtered = { ...prev };
                (tabRequiredFields[nextTab] || []).forEach((f) => delete filtered[f]);
                return filtered;
            });
            setActiveSection(nextTab);
        }
    };

    const goToPreviousTab = () => {
        const currentIndex = sections.findIndex((s) => s.id === activeSection);
        if (currentIndex > 0) {
            setActiveSection(sections[currentIndex - 1].id);
            setErrors({});
        }
    };

    const responsiblePersonOptions = [
        { value: "Bharat", label: "Bharat" },
        { value: "Harendra", label: "Harendra" },
        { value: "Sahil", label: "Sahil" },
        { value: "Khushboo", label: "Khushboo" },
        { value: "Sumit", label: "Sumit" },
    ];

    const departmentOptions = [
        { value: "NSV Dept", label: "NSV Dept" },
        { value: "FWD Dept", label: "FWD Dept" },
        { value: "MBIU", label: "MBIU" },
        { value: "Retro Reflectivity", label: "Retro Reflectivity" },
        { value: "Lab Dept", label: "Lab Dept" },
        { value: "ATCC", label: "ATCC" },
    ];

    useEffect(() => {
        const { completion_date, final_report_submission_date } = formData;
        if (completion_date && final_report_submission_date) {
            const start = new Date(completion_date);
            const end = new Date(final_report_submission_date);
            const diffDays = Math.round((end - start) / (1000 * 60 * 60 * 24));
            setFormData((prev) => ({ ...prev, total_duration: diffDays >= 0 ? diffDays : "0" }));
        } else {
            setFormData((prev) => ({ ...prev, total_duration: "0" }));
        }
    }, [formData.completion_date, formData.final_report_submission_date]);

    const isSectionComplete = (tabId) => getRequiredFields(tabId).every((field) => formData[field]);

    const sectionProgress = useMemo(() => {
        const completed = sections.filter((s) => isSectionComplete(s.id)).length;
        return Math.round((completed / sections.length) * 100);
    }, [formData, sections]);

    const hasCurrentTabErrors = () => getRequiredFields(activeSection).some((field) => errors[field]);

    const getCurrentTabErrorMessage = () => {
        const missingFields = getRequiredFields(activeSection)
            .filter((field) => !formData[field])
            .map((field) => fieldLabels[field] || field);
        if (missingFields.length === 0) return null;
        if (missingFields.length === 1) return `${missingFields[0]} is required to proceed`;
        return `${missingFields.length} fields required: ${missingFields.join(", ")}`;
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (activeSection !== 'remarks') { goToNextTab(); return; }
        if (!validateForm()) return;
        setLoading(true);
        const loadingToastId = toast.loading("Loading: Please wait...");
        try {
            const payload = {
                ...formData,
                test_planned_date: formData.test_planned_status ? formData.test_planned_date : null,
                revised_date: formData.report_revised_status ? formData.revised_date : null,
                revised_reason: formData.report_revised_status ? formData.revised_reason : "",
            };
            const res = await axios.put(`${BASE_URL}/labmonitoring/labjob/${i.id}/`, payload);
            if (res.status === 200 || res.status === 201) {
                toast.success("Project updated successfully!");
                handleClose();
                await getProjects?.();
            } else { alert(res); }
        } catch (err) { handleErrorToast(err); }
        finally { setLoading(false); toast.dismiss(loadingToastId); }
    };

    /* ================= BASIC INFO ================= */
    const renderBasicInfo = () => (
        <div className="form-section animate-fade-in">
            <div className="section-title">
                <SectionIcon icon={Info} color="#3b82f6" />
                <div>
                    <h4>Basic Information</h4>
                    <p className="section-subtitle">Core details about the project</p>
                </div>
            </div>
            <div className="form-grid">
                <div className="form-field">
                    <label><Hash size={14} /> Project Code</label>
                    <input type="text" name="project_code" placeholder="e.g. LAB/0262" onChange={handleInputChange} value={formData.project_code} className={errors.project_code ? "error" : inputState.project_code ? "success" : ""} />
                    {errors.project_code && <span className="error-message"><AlertCircle size={12} /> {errors.project_code}</span>}
                </div>

                <div className="form-field">
                    <label><FileText size={14} /> Project Name<span className="required">*</span></label>
                    <input type="text" name="project_name" placeholder="e.g. Jatinga-Assam" onChange={handleInputChange} value={formData.project_name} className={errors.project_name ? "error" : inputState.project_name ? "success" : ""} />
                    {errors.project_name && <span className="error-message"><AlertCircle size={12} /> {errors.project_name}</span>}
                </div>

                <div className="form-field">
                    <label><Target size={14} /> Project Status<span className="required">*</span></label>
                    <Select
                        options={projectStatusOptions}
                        isSearchable
                        placeholder="Select Status"
                        value={projectStatusOptions.find((opt) => opt.value === formData.project_status) || null}
                        onChange={(selected) => {
                            setFormData((prev) => ({ ...prev, project_status: selected?.value || "" }));
                            setInputState((prev) => ({ ...prev, project_status: selected ? "green" : "" }));
                            if (selected) {
                                setErrors((prev) => {
                                    const updated = { ...prev };
                                    delete updated.project_status;
                                    return updated;
                                });
                            }
                        }}
                        styles={CSSForSelectModern}
                    />
                    {errors.project_status && <span className="error-message"><AlertCircle size={12} /> {errors.project_status}</span>}
                </div>

                <div className="form-field">
                    <label><Building2 size={14} /> Client Detail<span className="required">*</span></label>
                    <input type="text" name="client_detail" placeholder="e.g. Cube Highways" onChange={handleInputChange} value={formData.client_detail} className={errors.client_detail ? "error" : inputState.client_detail ? "success" : ""} />
                    {errors.client_detail && <span className="error-message"><AlertCircle size={12} /> {errors.client_detail}</span>}
                </div>

                <div className="form-field">
                    <label><UserRound size={14} /> Client Concern Person</label>
                    <input type="text" name="client_concern_person" placeholder="e.g. Mr. Rajesh Kumar" onChange={handleInputChange} value={formData.client_concern_person} />
                </div>

                <div className="form-field">
                    <label><FileText size={14} /> Project Scope</label>
                    <input type="text" name="project_scope" placeholder="e.g. NABL" onChange={handleInputChange} value={formData.project_scope} />
                </div>

                <div className="form-field full-width">
                    <label><PenLine size={14} /> Project Description</label>
                    <textarea name="project_description" placeholder="Describe the project..." onChange={handleInputChange} value={formData.project_description} rows={3} />
                </div>
            </div>

            <div className="section-navigation-buttons nav-buttons-wrapper">
                {tabValidationAttempted[activeSection] && hasCurrentTabErrors() && (
                    <div className="tab-error-message">
                        <AlertCircle size={14} />
                        <span>{getCurrentTabErrorMessage()}</span>
                    </div>
                )}
                <button type="button" className="btn-next" onClick={goToNextTab}>
                    Next: Testing Details <ChevronRight size={16} />
                </button>
            </div>
        </div>
    );

    /* ================= TESTING DETAILS ================= */
    const renderTestingDetails = () => (
        <div className="form-section animate-fade-in">
            <div className="section-title">
                <SectionIcon icon={FlaskConical} color="#8b5cf6" />
                <div>
                    <h4>Testing Details</h4>
                    <p className="section-subtitle">Equipment, personnel and test planning</p>
                </div>
            </div>
            <div className="form-grid">
                <div className="form-field">
                    <label><Building2 size={14} /> Department / Equipment</label>
                    <Select
                        options={departmentOptions}
                        isSearchable
                        isClearable
                        placeholder="Select Department"
                        value={departmentOptions.find((opt) => opt.value === formData.department_equipment) || null}
                        onChange={(selected) => {
                            const value = selected?.value || "";
                            setFormData((prev) => ({
                                ...prev,
                                department_equipment: value,
                                lab_sample_quantity: value === "Lab Dept" ? prev.lab_sample_quantity : "0",
                            }));
                            setInputState((prev) => ({
                                ...prev,
                                department_equipment: selected ? "green" : "",
                                ...(value === "Lab Dept" ? {} : { lab_sample_quantity: "0" }),
                            }));
                        }}
                        styles={CSSForSelectModern}
                    />
                    {errors.department_equipment && <span className="error-message"><AlertCircle size={12} /> {errors.department_equipment}</span>}
                </div>

                {formData.department_equipment === "Lab Dept" && (
                    <div className="form-field conditional-field">
                        <label><Hash size={14} /> Lab Sample Quantity</label>
                        <input type="number" name="lab_sample_quantity" placeholder="e.g. 2" onChange={handleInputChange} value={formData.lab_sample_quantity} />
                    </div>
                )}

                <div className="form-field">
                    <label><Users size={14} /> Responsible Person<span className="required">*</span></label>
                    <Select
                        options={responsiblePersonOptions}
                        isSearchable
                        isClearable
                        placeholder="Select Responsible Person"
                        value={responsiblePersonOptions.find((opt) => opt.value === formData.responsible_person) || null}
                        onChange={(selected) => {
                            const value = selected?.value || "";
                            setFormData((prev) => ({ ...prev, responsible_person: value }));
                            setInputState((prev) => ({ ...prev, responsible_person: selected ? "green" : "" }));
                            setErrors((prev) => {
                                const updated = { ...prev };
                                if (value) delete updated.responsible_person;
                                return updated;
                            });
                        }}
                        styles={CSSForSelectModern}
                    />
                    {errors.responsible_person && <span className="error-message"><AlertCircle size={12} /> {errors.responsible_person}</span>}
                </div>

                <div className="form-field full-width">
                    <label><Users size={14} /> Testing Person(s)</label>
                    <input type="text" name="testing_person" placeholder="e.g. Sahil Sharma, Neeraj" onChange={handleInputChange} value={formData.testing_person} />
                </div>

                <div className="form-field full-width">
                    <ToggleSwitch
                        checked={formData.test_planned_status}
                        onChange={handleToggle("test_planned_status")}
                        label="Test Planned"
                        description="Turn on to set the planned test date"
                        icon={Clock}
                        color="#8b5cf6"
                    />
                </div>

                {formData.test_planned_status && (
                    <div className="form-field conditional-field">
                        <label><Calendar size={14} /> Test Planned Date</label>
                        <input type="date" name="test_planned_date" onChange={handleInputChange} value={formData.test_planned_date || ""} />
                    </div>
                )}
            </div>

            <div className="section-navigation-buttons dual-buttons">
                <div className="nav-buttons-wrapper">
                    <button type="button" className="btn-prev" onClick={goToPreviousTab}>
                        <ChevronRight size={16} className="rotate-left" /> Previous
                    </button>
                    {tabValidationAttempted[activeSection] && hasCurrentTabErrors() && (
                        <div className="tab-error-message">
                            <AlertCircle size={14} />
                            <span>{getCurrentTabErrorMessage()}</span>
                        </div>
                    )}
                    <button type="button" className="btn-next" onClick={goToNextTab}>
                        Next <ChevronRight size={16} />
                    </button>
                </div>
            </div>
        </div>
    );

    /* ================= TIMELINE ================= */
    const renderTimeline = () => (
        <div className="form-section animate-fade-in">
            <div className="section-title">
                <SectionIcon icon={Calendar} color="#f59e0b" />
                <div>
                    <h4>Timeline & Report Details</h4>
                    <p className="section-subtitle">Test info, dates and report revision</p>
                </div>
            </div>

            {/* Test Information */}
            <div className="form-subsection">
                <h5 className="subsection-title"><FlaskConical size={14} /> Test Information</h5>
                <div className="form-grid">
                    <div className="form-field full-width">
                        <label><FlaskConical size={14} /> Test Performed<span className="required">*</span></label>
                        <input type="text" name="test_performed" placeholder="e.g. UPV & Crack Depth" onChange={handleInputChange} value={formData.test_performed} className={errors.test_performed ? "error" : inputState.test_performed ? "success" : ""} />
                        {errors.test_performed && <span className="error-message"><AlertCircle size={12} /> {errors.test_performed}</span>}
                    </div>
                </div>
            </div>

            {/* Project Timeline */}
            <div className="form-subsection">
                <h5 className="subsection-title"><Calendar size={14} /> Project Timeline</h5>
                <div className="form-grid">
                    <div className="form-field">
                        <label><Calendar size={14} /> Project Confirmation Date</label>
                        <input type="date" name="project_confirmation_date" onChange={handleInputChange} value={formData.project_confirmation_date || ""} />
                    </div>

                    <div className="form-field">
                        <label><Calendar size={14} /> Mobilization Date</label>
                        <input type="date" name="mobilization_date" min={formData.project_confirmation_date || undefined} onChange={handleInputChange} value={formData.mobilization_date || ""} />
                    </div>

                    <div className="form-field">
                        <label><Calendar size={14} /> Survey Date <span className="required">*</span></label>
                        <input type="date" name="survey_date" min={formData.mobilization_date || undefined} onChange={handleInputChange} value={formData.survey_date || ""} className={errors.survey_date ? "error" : ""} />
                        {errors.survey_date && <span className="error-message"><AlertCircle size={12} /> {errors.survey_date}</span>}
                    </div>

                    <div className="form-field">
                        <label><Calendar size={14} /> Completion Date</label>
                        <input type="date" name="completion_date" min={formData.survey_date || undefined} onChange={handleInputChange} value={formData.completion_date || ""} />
                    </div>
                    <div className="form-field">
                        <label><Calendar size={14} /> Sample Receiving Date (HO)</label>
                        <input type="date" name="sample_receiving_date_ho" onChange={handleInputChange} value={formData.sample_receiving_date_ho || ""} />
                    </div>

                    <div className="form-field">
                        <label><Calendar size={14} /> Project Data Received From Client</label>
                        <input type="date" name="project_data_received_from_client" onChange={handleInputChange} value={formData.project_data_received_from_client || ""} />
                    </div>
                    <div className="form-field">
                        <label><Clock size={14} /> Total Duration (Days)</label>
                        <input type="number" name="total_duration" placeholder="Auto-calculated" value={formData.total_duration} readOnly disabled style={{ backgroundColor: "#f8fafc", cursor: "not-allowed", fontStyle: "italic" }} />
                    </div>
                    <div className="form-field">
                        <label><FileText size={14} /> Draft Report Submission Date</label>
                        <input type="date" name="draft_report_submission_date" onChange={handleInputChange} value={formData.draft_report_submission_date || ""} />
                    </div>

                    <div className="form-field">
                        <label><FileCheck size={14} /> Final Report Submission Date</label>
                        <input type="date" name="final_report_submission_date" min={formData.completion_date || undefined} onChange={handleInputChange} value={formData.final_report_submission_date || ""} />
                    </div>





                </div>
            </div>

            {/* Report Revision */}
            <div className="form-subsection">
                <h5 className="subsection-title"><ClipboardList size={14} /> Report Revision</h5>
                <div className="form-grid">
                    <div className="form-field full-width">
                        <ToggleSwitch
                            checked={formData.report_revised_status}
                            onChange={handleToggle("report_revised_status")}
                            label="Report Revised"
                            description="Turn on to add revised date and reason"
                            icon={Sparkles}
                            color="#f59e0b"
                        />
                    </div>

                    {formData.report_revised_status && (
                        <>
                            <div className="form-field conditional-field">
                                <label><Calendar size={14} /> Revised Date<span className="required">*</span></label>
                                <input type="date" name="revised_date" onChange={handleInputChange} value={formData.revised_date || ""} className={errors.revised_date ? "error" : ""} />
                                {errors.revised_date && <span className="error-message"><AlertCircle size={12} /> {errors.revised_date}</span>}
                            </div>

                            <div className="form-field conditional-field">
                                <label><FileText size={14} /> Revised Reason<span className="required">*</span></label>
                                <input type="text" name="revised_reason" placeholder="e.g. Client comments incorporated" onChange={handleInputChange} value={formData.revised_reason} className={errors.revised_reason ? "error" : inputState.revised_reason ? "success" : ""} />
                                {errors.revised_reason && <span className="error-message"><AlertCircle size={12} /> {errors.revised_reason}</span>}
                            </div>
                        </>
                    )}
                </div>
            </div>

            <div className="section-navigation-buttons dual-buttons">
                <div className="nav-buttons-wrapper">
                    <button type="button" className="btn-prev" onClick={goToPreviousTab}>
                        <ChevronRight size={16} className="rotate-left" /> Previous
                    </button>
                    {tabValidationAttempted[activeSection] && hasCurrentTabErrors() && (
                        <div className="tab-error-message">
                            <AlertCircle size={14} />
                            <span>{getCurrentTabErrorMessage()}</span>
                        </div>
                    )}
                    <button type="button" className="btn-next" onClick={goToNextTab}>
                        Next <ChevronRight size={16} />
                    </button>
                </div>
            </div>
        </div>
    );



    /* ================= REMARKS ================= */
    const renderRemarks = () => (
        <div className="form-section animate-fade-in">
            <div className="section-title">
                <SectionIcon icon={MessageSquare} color="#10b981" />
                <div>
                    <h4>Remarks</h4>
                    <p className="section-subtitle">Add additional notes and observations</p>
                </div>
            </div>

            <div className="form-grid">
                <div className="form-field full-width">
                    <label><FileText size={14} /> Remark 1</label>
                    <textarea name="remark_1" placeholder="Add remark..." onChange={handleInputChange} value={formData.remark_1} rows={2} />
                </div>

                <div className="form-field full-width">
                    <label><FileText size={14} /> Remark 2</label>
                    <textarea name="remark_2" placeholder="Add remark..." onChange={handleInputChange} value={formData.remark_2} rows={2} />
                </div>

                <div className="form-field full-width">
                    <div className="remarks-section">
                        <div className="remarks-header">
                            <MessageSquare size={16} />
                            <h5>Additional Remarks</h5>
                            <span className="remarks-count">{formData.remarks.length}</span>
                        </div>

                        <div className="remark-composer">
                            <input
                                type="date"
                                value={newRemark.date}
                                onChange={(e) => {
                                    setNewRemark((prev) => ({ ...prev, date: e.target.value }));
                                    if (remarkError) setRemarkError("");
                                }}
                            />
                            <textarea
                                rows={2}
                                placeholder="Write a remark..."
                                value={newRemark.remarks}
                                onChange={(e) => {
                                    setNewRemark((prev) => ({ ...prev, remarks: e.target.value }));
                                    if (remarkError) setRemarkError("");
                                }}
                                className={remarkError ? "error" : ""}
                            />
                            <button type="button" className="btn-add-remark" onClick={handleAddRemark}>
                                <Plus size={16} /> Add
                            </button>
                        </div>
                        {remarkError && <span className="error-message"><AlertCircle size={12} /> {remarkError}</span>}

                        {formData.remarks.length === 0 ? (
                            <div className="remark-empty">
                                <MessageSquare size={28} />
                                <p>No remarks added yet</p>
                                <small>Add your first remark using the composer above</small>
                            </div>
                        ) : (
                            <div className="remark-table-wrapper">
                                <table className="remark-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: "50px" }}>#</th>
                                            <th style={{ width: "140px" }}>Date</th>
                                            <th>Remark</th>
                                            <th style={{ width: "60px" }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {formData.remarks.map((r, i) => (
                                            <tr key={r.id}>
                                                <td>{i + 1}</td>
                                                <td>
                                                    <span className="remark-date"><Calendar size={12} /> {formatDate(r.date)}</span>
                                                </td>
                                                <td className="remark-text">{r.remarks}</td>
                                                <td>
                                                    <button type="button" className="remark-delete" title="Delete remark" onClick={() => handleDeleteRemark(r.id)}>
                                                        <Trash2 size={15} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    const renderCurrentSection = () => {
        switch (activeSection) {
            case 'basic': return renderBasicInfo();
            case 'testing': return renderTestingDetails();
            case 'timeline': return renderTimeline();
            case 'remarks': return renderRemarks();
            default: return renderBasicInfo();
        }
    };

    return (
        <>
            <UpdateModalButton onClick={handleShow} title="Update Project" />
            <Modal show={show} onHide={handleClose} dialogClassName="modern-project-modal" size="xl">
                <Modal.Header className="modern-modal-header">
                    <div className="header-content">
                        <div className="header-icon-wrapper">
                            <FlaskConical size={24} />
                        </div>
                        <div>
                            <Modal.Title>Update Project Details</Modal.Title>
                            <p className="header-subtitle">Update the details of this project</p>
                        </div>
                    </div>
                    <button className="close-btn" onClick={handleClose}>
                        <X size={20} />
                    </button>
                </Modal.Header>

                <Modal.Body className="modern-modal-body">
                    {/* Progress Bar */}
                    <div className="clean-progress-wrap">
                        <div className="clean-progress-info">
                            <span className="clean-progress-label">Progress</span>
                            <span className="clean-progress-percent">{sectionProgress}%</span>
                        </div>
                        <div className="clean-progress-bar">
                            <div
                                className="clean-progress-fill"
                                style={{ width: `${sectionProgress}%` }}
                            />
                        </div>
                    </div>

                    {/* Section Navigation */}
                    <div className="section-navigation">
                        {sections.map((section, index) => {
                            const Icon = section.icon;
                            const isComplete = isSectionComplete(section.id);
                            const hasMissingRequiredFields = getRequiredFields(section.id).some((field) => !formData[field]);
                            const isActive = activeSection === section.id;

                            return (
                                <React.Fragment key={section.id}>
                                    <button
                                        type="button"
                                        className={`section-nav-btn ${isActive ? 'active' : ''} ${isComplete ? 'complete' : ''} ${hasMissingRequiredFields && !isActive ? 'has-warning' : ''}`}
                                        style={{ "--section-color": section.color }}
                                        onClick={() => {
                                            if (activeSection !== section.id) {
                                                if (validateCurrentTab()) {
                                                    setActiveSection(section.id);
                                                    setErrors({});
                                                } else {
                                                    const formContainer = document.querySelector('.modern-modal-body');
                                                    if (formContainer) formContainer.scrollTop = 0;
                                                }
                                            }
                                        }}
                                    >
                                        <div className="nav-icon-wrap">
                                            {isComplete && !isActive ? <Check size={14} /> : <Icon size={15} />}
                                        </div>
                                        <span>{section.label}</span>
                                        {isActive && <ChevronRight size={14} className="nav-arrow" />}
                                    </button>
                                    {index < sections.length - 1 && <div className="nav-divider" />}
                                </React.Fragment>
                            );
                        })}
                    </div>

                    <form onSubmit={handleFormSubmit} className="project-form">
                        {renderCurrentSection()}
                    </form>
                </Modal.Body>

                {/* Sticky Footer for actions on remarks tab */}
                {activeSection === 'remarks' && (
                    <Modal.Footer className="modern-modal-footer">
                        <button type="button" className="btn-prev" onClick={goToPreviousTab}>
                            <ChevronRight size={16} className="rotate-left" /> Previous
                        </button>
                        <div className="submit-buttons">
                            <button type="button" className="btn-secondaryy" onClick={handleClose}>
                                Cancel
                            </button>
                            <button type="button" className="btn-primaryy" disabled={loading} onClick={handleFormSubmit}>
                                {loading ? <Loader size={18} className="spinning" /> : <Save size={18} />}
                                <span>{loading ? 'Updating...' : 'Update Project'}</span>
                            </button>
                        </div>
                    </Modal.Footer>
                )}
            </Modal>

            <ToastContainer
                position="top-center"
                autoClose={1000}
                hideProgressBar={false}
                newestOnTop={true}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
        </>
    );
};

const DeleteProjectDetails = ({ i, getData }) => {
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleShow = () => setShow(true);
    const handleClose = () => setShow(false);

    const handleDelete = async (e) => {
        e.preventDefault();
        setLoading(true);
        const loadingToastId = toast.loading("Loading: Please wait...");

        try {
            const response = await axios.delete(`${BASE_URL}/labmonitoring/labjob/${i.id}/`);
            if (response.status === 200) {
                getData();
                handleClose();
                toast.dismiss(loadingToastId);
                toast.success("Data deleted successfully");
            }
        } catch (err) {
            handleErrorToast(err, loadingToastId);
        } finally {
            setLoading(false);
            toast.dismiss(loadingToastId);
        }
    };

    return (
        <>
            <DeleteModalButton title="Delete Data" onClick={handleShow} />

            <Modal
                show={show}
                onHide={handleClose}
                dialogClassName="modern-project-modal"
                size="md"
                centered
            >
                {/* ===== HEADER ===== */}
                <Modal.Header className="modern-modal-header delete-modal-header">
                    <div className="header-content">
                        <div className="header-icon-wrapper delete-icon-wrapper">
                            <Trash2 size={22} />
                        </div>
                        <div>
                            <Modal.Title>Delete Record</Modal.Title>
                            <p className="header-subtitle">This action cannot be undone</p>
                        </div>
                    </div>
                    <button className="close-btn" onClick={handleClose}>
                        <X size={20} />
                    </button>
                </Modal.Header>

                {/* ===== BODY ===== */}
                <Modal.Body className="modern-modal-body">
                    <div className="delete-confirm-wrap">
                        {/* Warning Icon */}
                        <div className="delete-warning-icon">
                            <AlertTriangle size={34} />
                        </div>

                        {/* Heading */}
                        <h4 className="delete-confirm-title">
                            Are you sure you want to delete this record?
                        </h4>
                        <p className="delete-confirm-subtitle">
                            All data associated with this project will be permanently removed from the system.
                        </p>

                        {/* Project Preview Card */}
                        {(i?.project_name || i?.project_code || i?.client_detail) && (
                            <div className="delete-preview-card">
                                <div className="delete-preview-icon">
                                    <FlaskConical size={16} />
                                </div>
                                <div className="delete-preview-content">
                                    <div className="delete-preview-name">
                                        {i?.project_code && <span className="delete-preview-code">{i.project_code}</span>}
                                        {i?.project_code && i?.project_name && <span className="delete-preview-sep">•</span>}
                                        <span>{i?.project_name || 'Untitled Project'}</span>
                                    </div>
                                    <div className="delete-preview-meta">
                                        {i?.client_detail && (
                                            <span className="delete-preview-chip">
                                                <Building2 size={12} /> {i.client_detail}
                                            </span>
                                        )}
                                        {i?.responsible_person && (
                                            <span className="delete-preview-chip">
                                                <UserRound size={12} /> {i.responsible_person}
                                            </span>
                                        )}
                                        {i?.project_status && (
                                            <span className="delete-preview-chip">
                                                <Target size={12} /> {i.project_status}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Warning Note */}
                        <div className="delete-warning-note">
                            <AlertCircle size={14} />
                            <span>This will permanently delete the project and its remarks.</span>
                        </div>
                    </div>
                </Modal.Body>

                {/* ===== FOOTER ===== */}
                <Modal.Footer className="modern-modal-footer">
                    <button
                        type="button"
                        className="btn-secondaryy"
                        onClick={handleClose}
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleDelete}
                        className="btn-danger-delete"
                        disabled={loading}
                    >
                        {loading ? <Loader size={18} className="spinning" /> : <Trash2 size={18} />}
                        <span>{loading ? 'Deleting...' : 'Delete Project'}</span>
                    </button>
                </Modal.Footer>
            </Modal>

            <ToastContainer
                position="top-center"
                autoClose={1000}
                hideProgressBar={false}
                newestOnTop={true}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
        </>
    );
};
export { UpdateProjectDetails, DeleteProjectDetails };